/* istanbul ignore file */
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  BlockType,
  OccupancyType,
  PageType,
  RelationshipType,
  StudentSelectionMode,
} from '@common/entities';
import { hasWaitlistUnitConflict } from '@common/validation-schemas';
import { useMutation } from '@tanstack/react-query';
import { NotificationStatus } from 'enums/notification-status';
import { resetSaveState, resetSaveTrigger, setSaveCompleted } from 'state/slices/actionsSlice';
import { setExternalSyncErrors, updateLeasingJourneyErrors } from 'state/slices/navigationSlice';
import { setQuote } from 'state/slices/unitSelectionSlice';
import { RootState } from 'state/store';

import notification from 'components/notification';
import { useGetApplicantRelationship } from 'hooks/useGetApplicantRelationship';
import { useGetPageData } from 'hooks/useGetPageData';
import { useSaveResponse } from 'hooks/useSaveResponse';
import {
  getSecondaryApplicantSaveErrors,
  useSyncSecondaryApplicantsForPageSave,
} from 'hooks/useSyncSecondaryApplicantsForPageSave';
import { useWaitlistFunctions } from 'hooks/useWaitlistFunctions';
import { patchResponseForId } from 'services';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { resolveIsoDateValue } from 'utils/formatting/dateInputUtils';
import { getErrorCount } from 'utils/getErrors';
import { getDisplayableExternalSyncErrors } from 'utils/getExternalSyncErrors';
import handleError from 'utils/handleError';
import { getPageIndex } from 'utils/page-utils';

import { useStudentSelectionMode } from './useStudentSelectionMode';

interface Props {
  activeStepIndex: number;
  // When set (Lead Profile), the save is patched to this specific tab's response instead
  // of the active application response, matching the hybrid Options page pattern.
  responseId?: string;
}
export function useSaveFloorPlanPageData({ activeStepIndex, responseId }: Props) {
  const {
    getValues,
    getFieldState,
    formState: { errors },
  } = useFormContext();
  const saveResponse = useSaveResponse();
  const getPageData = useGetPageData();
  const syncSecondaryApplicantsForPageSave = useSyncSecondaryApplicantsForPageSave();
  const { getApplicantRelationship } = useGetApplicantRelationship();

  // Only the primary applicant selects a unit; co-applicants/guarantors just fill any
  // builder-added fields on the Floor Plans page.
  const isPrimary = getApplicantRelationship() === RelationshipType.Primary;

  // Lead Profile saves target a specific tab response (responseId) and must not touch the
  // quote/Offers flow, mirroring the legacy Lead Profile behavior.
  const isLeadProfileSave = !!responseId;

  const { propertyId } = useParams();
  const { isOnAnyWaitlist, isWaitlistEnabled } = useWaitlistFunctions();
  const studentSelectionMode = useStudentSelectionMode();

  const dispatch = useDispatch();
  const { currentTabIndex = 0 } = useSelector((state: RootState) => state.navigation ?? {});
  const { floorPlan, unit, leaseTerm, moveInDate, quote, quoteLeaseTerm, waitlistSelections } =
    useSelector((state: RootState) => state.unitSelection);
  const { occupancyType } = useSelector((state: RootState) => state.application.form);
  const {
    selectedSpace,
    selectedLeaseStartWindowId,
    selectedLeaseTermId,
    availableSpaceConfigurationIds,
    selectedPreferredAmenityIds,
    immediateMoveInMaxDate,
  } = useSelector((state: RootState) => state.studentUnitSelection);
  const oldFloorPlanPageResponse = useSelector(
    (state: RootState) => state?.response?.value?.floorPlanData,
  );
  const oldWaitlistResponse = useSelector(
    (state: RootState) => state?.response?.value?.waitlistSelections,
  );
  const {
    form: { pages },
    activeResponse: { applicationId },
    applicant,
  } = useSelector((state: RootState) => state.application);
  const { saveTriggerCounter = 0 } = useSelector((state: RootState) => state.actions || {});

  // Lead Profile does not carry a propertyId route param, so fall back to the applicant's
  // propertyId (same source the Options page uses in Lead Profile).
  const requestPropertyId = isLeadProfileSave ? applicant?.propertyId : Number(propertyId);
  const floorPlanPageHasInstallmentPlans = pages[activeStepIndex]?.sections?.some((section: any) =>
    section.elements?.some((element: any) => element.customType === BlockType.InstallmentPlans),
  );

  // Lead Profile patches a specific tab response and mirrors the Options page side effects
  // (save-completed signalling and manual-save notification).
  const { mutateAsync: updateResponseForId } = useMutation({
    mutationFn: patchResponseForId,
    onSuccess: () => {
      dispatch(setSaveCompleted(true));
      if (saveTriggerCounter > 0) {
        dispatch(resetSaveTrigger());
        notification('Save successful', NotificationStatus.SUCCESS);
      }
    },
    onError: (error) => {
      dispatch(resetSaveState());
      const errorMessage = extractErrorMessage(error);
      handleError(`Form Viewer - ${errorMessage}: ${error}`, `${errorMessage}`);
    },
  });

  // Check if we're in student bulk assignment mode
  const isStudentBulkMode =
    occupancyType === OccupancyType.Student &&
    studentSelectionMode === StudentSelectionMode.BULK_UNIT_ASSIGNMENT;

  const hasSelectionConflict = hasWaitlistUnitConflict({
    isWaitlistEnabled,
    isOnAnyWaitlist: isOnAnyWaitlist(),
    floorPlan,
    unit,
    leaseTerm,
  });

  function validateFloorPlanPage() {
    if (hasSelectionConflict) {
      return 'You cannot select a unit if you are on a waitlist. Please remove yourself from waitlists to select a unit.';
    }

    // Student bulk assignment mode validation
    if (isStudentBulkMode) {
      const hasFloorPlan = !!floorPlan;
      const hasSpaceOption = availableSpaceConfigurationIds.length > 0;
      const hasLeaseTerm = !!selectedLeaseTermId;

      if (!isWaitlistEnabled && (!hasFloorPlan || !hasSpaceOption || !hasLeaseTerm)) {
        return 'Please select a floor plan, space option, and lease term.';
      }
      if (
        isWaitlistEnabled &&
        !isOnAnyWaitlist() &&
        (!hasFloorPlan || !hasSpaceOption || !hasLeaseTerm)
      ) {
        return 'Please select a floor plan, space option, and lease term, or join a waitlist.';
      }
      return '';
    }

    // Standard validation for other modes
    if (!isWaitlistEnabled && (!floorPlan || !unit || !leaseTerm)) {
      return 'Please select a floor plan, unit and lease term.';
    }
    if (isWaitlistEnabled && !isOnAnyWaitlist() && (!floorPlan || !unit || !leaseTerm)) {
      return 'Please select a floor plan, unit and lease term, or join a waitlist.';
    }

    return '';
  }

  function setFloorPlanStepsError(errorCount = 0) {
    dispatch(
      updateLeasingJourneyErrors({
        pageIndex: activeStepIndex,
        count: errorCount,
      }),
    );
  }

  function getFloorPlanPageSelectionData() {
    const response: any = {};

    // Student bulk assignment mode - no unit selection required
    if (isStudentBulkMode && floorPlan && availableSpaceConfigurationIds.length > 0) {
      response.floorPlanData = {
        floorplanId: floorPlan.id,
        spaceConfigurationId: availableSpaceConfigurationIds[0],
        leaseTermId: selectedLeaseTermId ? Number(selectedLeaseTermId) : undefined,
        leaseStartWindowId: selectedLeaseStartWindowId
          ? Number(selectedLeaseStartWindowId)
          : undefined,
        moveInDate,
        preferredAmenityIds: selectedPreferredAmenityIds,
      };
    } else if (floorPlan && unit && leaseTerm) {
      // Standard mode - unit selection required
      // Determine the correct unitSpaceId based on occupancy type
      const isStudentOccupancy = occupancyType === OccupancyType.Student;
      const unitSpaceId =
        isStudentOccupancy && selectedSpace ? Number(selectedSpace.unitSpaceId) : unit.unitSpaceId;

      response.floorPlanData = {
        floorplanId: floorPlan?.id,
        unitId: unit?.id,
        unitSpaceId,
        leaseTermId: leaseTerm?.id,
        termMonth: leaseTerm?.termMonth,
        moveInDate,
        preferredAmenityIds: selectedPreferredAmenityIds,
        // Add spaceConfigurationId and leaseStartWindowId for student occupancy
        ...(isStudentOccupancy &&
          selectedSpace && {
            spaceConfigurationId: selectedSpace.spaceConfigurationId,
            leaseStartWindowId: selectedLeaseStartWindowId,
          }),
      };
    } else if (oldFloorPlanPageResponse) {
      const {
        floorplanId,
        unitId,
        unitSpaceId,
        leaseTermId,
        termMonth,
        preferredAmenityIds,
        spaceConfigurationId,
        leaseStartWindowId,
      } = oldFloorPlanPageResponse;

      response.floorPlanData = {
        floorplanId,
        unitId,
        unitSpaceId,
        leaseTermId,
        termMonth,
        moveInDate,
        preferredAmenityIds: preferredAmenityIds || selectedPreferredAmenityIds,
        spaceConfigurationId,
        leaseStartWindowId,
      };
    }

    if (waitlistSelections) {
      response.waitlistSelections = waitlistSelections;
    } else if (oldWaitlistResponse) {
      response.waitlistSelections = oldWaitlistResponse;
    }

    const installmentPlans = getFloorPlanInstallmentPlanSelection();
    if (
      installmentPlans &&
      typeof installmentPlans === 'object' &&
      installmentPlans.selectedPlan !== undefined &&
      installmentPlans.selectedPlan !== ''
    ) {
      response[BlockType.InstallmentPlans] = installmentPlans;
    }

    return response;
  }

  function getFloorPlanInstallmentPlanSelection() {
    if (!floorPlanPageHasInstallmentPlans) return undefined;

    return getValues(BlockType.InstallmentPlans);
  }

  function hasFloorPlanInstallmentPlanSelection() {
    const installmentPlans = getFloorPlanInstallmentPlanSelection();

    return (
      installmentPlans &&
      typeof installmentPlans === 'object' &&
      installmentPlans.selectedPlan !== undefined &&
      installmentPlans.selectedPlan !== ''
    );
  }

  /**
   * Collect responses for builder-added fields/blocks on the Floor Plans page via the
   * shared page-data collector (same as blank/options pages). InstallmentPlans is owned
   * by the floor-plan selection payload, so it is stripped here to avoid duplication or
   * overwriting a saved plan with an empty object.
   */
  function getAddedFieldResponses(): { [key: string]: any }[] {
    const pageData = getPageData(activeStepIndex) || [];

    return pageData
      .map((item) => {
        if (item && BlockType.InstallmentPlans in item) {
          const clone = { ...item };
          delete clone[BlockType.InstallmentPlans];
          return clone;
        }
        return item;
      })
      .filter((item) => item && Object.keys(item).length > 0);
  }

  function prepareFloorPlanRequest(
    targetIndex: number,
    errorCount = 0,
    addedFieldResponses = getAddedFieldResponses(),
  ) {
    const responses = isPrimary
      ? [getFloorPlanPageSelectionData(), ...addedFieldResponses]
      : addedFieldResponses;

    return {
      currentPageId: pages[activeStepIndex].id,
      targetPageId: pages[targetIndex].id,
      applicationId,
      responses,
      propertyId: requestPropertyId,
      errorCount,
    };
  }

  const updateQuotes = async () => {
    const offersPageIndex = getPageIndex(pages, PageType.Offers);
    if (offersPageIndex < 0) return;
    try {
      await saveResponse({
        currentPageId: pages[offersPageIndex]?.id,
        applicationId,
        propertyId: requestPropertyId,
        responses: [],

        errorCount: 0,
      });
      dispatch(setQuote({ quote: null, quoteLeaseTerm: null, quoteUnitSpaceId: null }));
    } catch (err: any) {
      throw new Error(err);
    }
  };

  const shouldProceedTheRequest = () => {
    const offersPageIndex = getPageIndex(pages, PageType.Offers);
    if (offersPageIndex < 0) return true;
    if (!quote || !quoteLeaseTerm) return true;
    const { floorPlanData } = getFloorPlanPageSelectionData();
    if (!floorPlanData) return true;
    const { floorplanId, unitSpaceId, leaseTermId, spaceConfigurationId } = floorPlanData as any;

    // For bulk mode, unitSpaceId may not exist
    if (isStudentBulkMode) {
      return true;
    }

    const {
      unitDetails: { id, floorId },
    } = quote;
    const savedSpaceConfigurationId = oldFloorPlanPageResponse?.spaceConfigurationId;
    const isSpaceConfigurationUnchanged =
      (savedSpaceConfigurationId == null ? null : String(savedSpaceConfigurationId)) ===
      (spaceConfigurationId == null ? null : String(spaceConfigurationId));

    if (
      quoteLeaseTerm === Number(leaseTermId) &&
      floorplanId === floorId &&
      unitSpaceId === id &&
      isSpaceConfigurationUnchanged
    ) {
      return false;
    }
    return true;
  };

  function showWarning(message: string) {
    window.scrollTo(0, 0);
    notification(message, NotificationStatus.WARNING);
  }

  async function saveFloorPlanPage(targetIndex: number) {
    // Added-field validation errors count for every applicant type.
    let errorCount = getErrorCount(errors);
    const initialAddedFieldErrorCount = errorCount;

    // Unit-selection validation only applies to the primary applicant.
    if (isPrimary) {
      const selectedMoveInDate = resolveIsoDateValue(moveInDate, undefined, 'entrata');
      if (
        immediateMoveInMaxDate &&
        selectedMoveInDate &&
        selectedMoveInDate > immediateMoveInMaxDate
      ) {
        setFloorPlanStepsError(getErrorCount(errors) + 1);
        return false;
      }

      const errormsg = validateFloorPlanPage();
      if (errormsg?.length > 0) {
        errorCount += 1;

        if (targetIndex > activeStepIndex && hasSelectionConflict) {
          setFloorPlanStepsError(errorCount);
          showWarning(errormsg);
          return false;
        }
      }
    }

    // shouldProceedTheRequest() returns false only when the primary's unit selection is
    // unchanged from the saved quote. That signal governs BOTH whether we can skip and
    // whether the quote must be invalidated.
    const unitSelectionChanged = isPrimary && shouldProceedTheRequest();
    const hasAddedFieldData = getAddedFieldResponses().length > 0;
    const hasInstallmentPlanSelection = hasFloorPlanInstallmentPlanSelection();

    // Skip only when there is nothing to persist: unchanged selection, no added fields, and no
    // installment plan selection. InstallmentPlans is deliberately excluded from the generic
    // added-field response collection because it is included in the floor-plan payload.
    // Lead Profile is exempt: LPController waits on setSaveCompleted(true) before switching
    // tabs / completing a manual Save, so we must always run the save cycle there (the
    // updateResponseForId mutation dispatches setSaveCompleted and resets the save trigger).
    if (
      !isLeadProfileSave &&
      !unitSelectionChanged &&
      !hasAddedFieldData &&
      !hasInstallmentPlanSelection
    ) {
      return true;
    }

    try {
      const pageResponses = await syncSecondaryApplicantsForPageSave(
        getAddedFieldResponses(),
        pages[activeStepIndex].id,
      );
      errorCount +=
        getErrorCount(getSecondaryApplicantSaveErrors(errors, getFieldState)) -
        initialAddedFieldErrorCount;
      const requestData = prepareFloorPlanRequest(targetIndex, errorCount, pageResponses);
      let updateResult;
      if (isLeadProfileSave) {
        // Lead Profile persists to the active tab's response and never mutates the quote/
        // Offers flow (matching the legacy Lead Profile floor-plan behavior).
        updateResult = await updateResponseForId({ responseId, responseData: requestData });
      } else {
        updateResult = await saveResponse(requestData);
      }

      const externalSyncErrors = getDisplayableExternalSyncErrors(
        updateResult?.data?.externalSync,
        errorCount,
      );
      dispatch(
        setExternalSyncErrors({
          tabIndex: isLeadProfileSave ? currentTabIndex : 0,
          pageId: requestData.currentPageId,
          errors: externalSyncErrors,
        }),
      );
      const syncErrorCount = externalSyncErrors.length > 0 ? Math.max(errorCount, 1) : errorCount;

      // Only wipe the saved Offers response / Redux quote when the selection changed.
      if (!isLeadProfileSave && unitSelectionChanged) {
        await updateQuotes();
      }
      setFloorPlanStepsError(syncErrorCount);
      return true;
    } catch (error: any) {
      if (isLeadProfileSave) {
        dispatch(resetSaveState());
      }
      handleError(`Form Viewer - Error saving floor plan page: ${error}`, `${error.message}`);
      setFloorPlanStepsError(errorCount);
      return false;
    }
  }

  return saveFloorPlanPage;
}
